import { BrowserSupport } from './BrowserSupport';

/**
 * This handles user agent assertions, detecting things about the OS and browser.
 *
 * BEFORE YOU ADD ANYTHING TO THIS CLASS:
 * Can you what you're trying to do with feature detection? Double check! If you can, put it in BrowserSupport.ts.
 * That's the preferred method over looking at the user agent.
 *
 */
export module UserAgentSupport {

  const MSIE_REGEX = /msie ([\w.]+)/i; //This check only works for versions of IE < 11
  const EDGE_REGEX = /Edge/;
  // Keep minimum touch check in sync with workgroup\vqlweb\scriptsharp\src\Core\Utility\BrowserSupport.cs
  const TOUCH_REGEX = /iphone|ipad|ipod|android|blackberry|webos|iemobile|palm/i;
  const IOS_REGEX = /iphone|ipad|ipod/i;
  const ANDROID_REGEX = /android/i;
  const MAC_REGEX = /Mac/;
  const WINDOWS_REGEX = /Windows/;
  const WINDOWS_64_BIT_REGEX = /Win64|WOW64|amd64|x86_64|x86-64/i;
  const IOS_BROWSERS = /Safari|CriOS/i;
  const FIREFOX = /Firefox/i;
  const CHROME = /Chrome/i;
  // The following two regex are to be used to distinguish android chrome vs android webview chrome
  // https://developer.chrome.com/multidevice/user-agent#webview_user_agent
  const VERSION_REGEX = /Version\/[0-9]+\.[0-9]+/i;
  const CHROME_WEBVIEW_REGEX = /wv/i;
  const IE11_REGEX = /Trident/;
  const SAFARI_REGEX = /Safari/;
  const MACINTOSH_REGEX = /Macintosh/;
  const CRI_OS_REGEX = /CriOS/;
  const TABLEAU_REGEX = /Tableau/;

  export function isIE11() {
    return cachedIsIE11;
  }

  export function isIE9orLess() {
    return cachedIsIE9OrLess;
  }

  export function isEdge() {
    return cachedIsEdge;
  }

  export function isTouch() {
    return cachedIsTouch;
  }

  export function isIOS(): boolean {
    return cachedIsIOS;
  }

  export function isAndroid(): boolean {
    return cachedIsAndroid;
  }

  export function isMac() {
    return cachedIsMac;
  }

  export function isWindows() {
    return cachedIsWindows;
  }

  export function isMobileBrowser(){
    return cachedIsMobileBrowser;
  }

  // This just checks for "Tableau" in the user agent. Tableau Apps typically will include it (e.g. Phoenix, Tableau Desktop)
  // Caution: There may be Tableau Applicaitons that do not include this. Please manually verify if unsure. 
  export function isTableau(){
    return cachedIsTableau;
  }

  /**
   * Macs don't report 32/64 bit.
   *
   * These may also match on linux. That behavior is untested.
   */
  export function is64BitWindows() {
    return cachedIs64BitWindows;
  }

  export function isSafariOnMac() {
    return cachedIsSafariOnMac;
  }

  /**
   * It's more convenient in client code to not have to inject the user agents into all of these functions,
   * and also it's good to precompute the results of the regexes since they'll always be the same and regexes aren't
   * the fastest.
   *
   * However, it's important to be able to not cache and instead manually set the user agent in tests.
   * Real user agents are read only, and depending on the test runner, they may or may not be writable in tests.
   * (historical note: on a merge from hosted-main -> far, the runner user agent became read-only and broke this)
   * So to facilitate testing we just make it possible to pass specific user agents in.
   */
  export module Impl {

    /**
     * Macs don't report 32/64 bit.
     *
     * These may also match on linux. That behavior is untested.
     */
    export function is64BitWindows(userAgent: string) {
      return WINDOWS_64_BIT_REGEX.test(userAgent);
    }

    export function isWindows(userAgent: string) {
      return WINDOWS_REGEX.test(userAgent);
    }

    export function isMac(userAgent: string) {
      return MAC_REGEX.test(userAgent);
    }

    // This is legacy and untested. If you find an issue add a test
    export function isAndroid(userAgent: string) {
      return ANDROID_REGEX.test(userAgent);
    }

    // This is legacy and untested. If you find an issue add a test
    export function isTouch(userAgent: string) {
      return TOUCH_REGEX.test(userAgent);
    }

    // This is legacy and untested. If you find an issue add a test
    export function isIOS(userAgent: string) {
      return IOS_REGEX.test(userAgent);
    }

    // This is legacy and untested. If you find an issue add a test
    export function isEdge(userAgent: string) {
      return EDGE_REGEX.test(userAgent);
    }

    // This is legacy and untested. If you find an issue add a test
    export function isIE9OrLess(userAgent: string) {
      const msieMatch = MSIE_REGEX.exec(userAgent);

      // msieMatch[1] evaluates to '9.0', this will not compare as expected to '10'
      // Therefore, parseInt and compare as numbers instead.
      return !!msieMatch && parseInt(msieMatch[1], 10) < 9;
    }

    export function isIE11(userAgent: string) {
      return IE11_REGEX.test(userAgent);
    }

    export function isChrome(userAgent: string) {
      // Logic copied from BrowserSupport library
      return CRI_OS_REGEX.test(userAgent) || CHROME.test(userAgent);
    }

    export function IsSafari(userAgent: string) {
      // Logic copied from BrowserSupport library
      return SAFARI_REGEX.test(userAgent) && !isChrome(userAgent);
    }

    // Detects whether the request is from a browser from a mobile device. Note that 
    // this will return false if a request is coming from a web view on a mobile device.
    export function isMobileBrowser(userAgent: string) {
      const isMobileDevice = TOUCH_REGEX.test(userAgent);
      const isIOSBrowser = IOS_BROWSERS.test(userAgent) && IOS_REGEX.test(userAgent);
      const isFirefox = FIREFOX.test(userAgent);
      const isAndroidChrome = CHROME.test(userAgent) && ANDROID_REGEX.test(userAgent);
      // Logic is from here: https://developer.chrome.com/multidevice/user-agent#webview_user_agent
      const isAndroidChromeBrowser = isAndroidChrome &&
                              !VERSION_REGEX.test(userAgent) &&
                              !CHROME_WEBVIEW_REGEX.test(userAgent);
      return isMobileDevice && (isIOSBrowser || isFirefox || isAndroidChromeBrowser);
    }

    // If this returns true, then that means we are on a safari on mac. Note that this can also 
    // return true for ipad os can also since safari on ipad OS uses the same user agent.
    export function isSafariOnMac(userAgent: string) {
      return MACINTOSH_REGEX.test(userAgent) && IsSafari(userAgent);
    }

    export function isTableau(userAgent: string) {
      return TABLEAU_REGEX.test(userAgent);
    }
  }

  // In non-test scenarios, the user agent doesn't change so we cache the result
  // Also, these need to be declared below the Impl module.
  const cachedIsWindows: boolean = Impl.isWindows(window.navigator.userAgent);
  const cachedIs64BitWindows: boolean = Impl.is64BitWindows(window.navigator.userAgent);
  const cachedIsMac: boolean = Impl.isMac(window.navigator.userAgent);
  const cachedIsAndroid: boolean = Impl.isAndroid(window.navigator.userAgent);
  const cachedIsIOS: boolean = Impl.isIOS(window.navigator.userAgent);
  const cachedIsTouch: boolean = Impl.isTouch(window.navigator.userAgent);
  const cachedIsEdge: boolean = Impl.isEdge(window.navigator.userAgent);
  const cachedIsIE9OrLess: boolean = Impl.isIE9OrLess(window.navigator.userAgent);
  const cachedIsMobileBrowser: boolean = Impl.isMobileBrowser(window.navigator.userAgent);
  const cachedIsIE11: boolean = Impl.isIE11(window.navigator.userAgent);
  const cachedIsSafariOnMac: boolean = Impl.isSafariOnMac(window.navigator.userAgent);
  const cachedIsTableau: boolean = Impl.isTableau(window.navigator.userAgent);
}
